import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  // Client-readable, non-sensitive hint (NOT a credential): "a session may exist".
  // Set on any successful auth, cleared on logout / failed probe. Lets us skip the
  // /auth/me request for clearly logged-out public visitors (removes 401 console noise).
  // The httpOnly access_token cookie remains the sole source of truth for real auth.
  useEffect(() => {
    try {
      if (user && user.user_id) localStorage.setItem("fv_auth_hint", "1");
      else if (user === false) localStorage.removeItem("fv_auth_hint");
    } catch (e) { /* ignore */ }
  }, [user]);

  // Once a session is active, flush any pending acquisition attribution (from a public
  // tool / lead signup). Runs once per authenticated user; safe no-op otherwise.
  useEffect(() => {
    if (!user || !user.user_id) return;
    let pending;
    try { pending = localStorage.getItem("fv_acq_pending"); } catch (e) { return; }
    if (!pending) return;
    try {
      api.post("/acquisition/link", JSON.parse(pending)).catch(() => {});
      localStorage.removeItem("fv_acq_pending");
    } catch (e) { /* ignore */ }
  }, [user]);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    // Skip the /auth/me probe for clearly logged-out visitors (no session hint) —
    // avoids the expected-but-noisy 401 on public pages. Real sessions still resolve
    // because a successful auth always sets the hint (see effect above).
    let hint = null;
    try { hint = localStorage.getItem("fv_auth_hint"); } catch (e) { /* ignore */ }
    if (!hint) {
      setUser(false);
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const value = { user, setUser, loading, refresh: checkAuth };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
